import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import NexusAlertsTable from '../../components/NexusAlertsTable';
import USNexusMap from '../../components/NexusMap';
import StateDetailPanel from '../../components/StateDetail';
import { useAuth } from '../../context/AuthContext';
import { useWindowWidth } from '../../hooks/useWindowWidth';
import { ApiError, type StateNexusData, nexusStatusApi, stacCountsApi } from '../../services/api';

interface Counts {
  triggered: number;
  watching: number;
}

function WarningTriangleIcon(): JSX.Element {
  return (
    <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none">
      <path
        d="M12.8659 3.00017L22.3922 19.5002C22.6684 19.9785 22.5045 20.5901 22.0262 20.8662C21.8742 20.954 21.7017 21.0002 21.5262 21.0002H2.47363C1.92135 21.0002 1.47363 20.5525 1.47363 20.0002C1.47363 19.8246 1.51984 19.6522 1.60761 19.5002L11.1339 3.00017C11.41 2.52187 12.0216 2.358 12.4999 2.63414C12.6519 2.72191 12.7782 2.84815 12.8659 3.00017ZM4.20568 19.0002H19.7941L11.9999 5.50017L4.20568 19.0002ZM10.9999 16.0002H12.9999V18.0002H10.9999V16.0002ZM10.9999 9.00017H12.9999V14.0002H10.9999V9.00017Z"
        fill="#E93544"
      />
    </svg>
  );
}

function TargetIcon(): JSX.Element {
  return (
    <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none">
      <path
        d="M12 1.99999C12.5523 1.99999 13 2.4477 13 2.99999C12.9999 3.55224 12.5522 3.99999 12 3.99999C7.58172 3.99999 4 7.58171 4 12C4.00004 16.4182 7.58174 20 12 20C16.4182 20 19.9999 16.4182 20 12C20 11.4477 20.4477 11 21 11C21.5523 11 22 11.4477 22 12C21.9999 17.5228 17.5228 22 12 22C6.47717 22 2.00004 17.5228 2 12C2 6.47714 6.47715 1.99999 12 1.99999ZM12 5.99999C12.5523 5.99999 13 6.4477 13 6.99999C12.9999 7.55224 12.5522 7.99999 12 7.99999C9.79085 7.99999 7.99999 9.79085 7.99999 12C8.00004 14.2091 9.79088 16 12 16C14.2091 16 15.9999 14.2091 16 12C16 11.4477 16.4477 11 17 11C17.5523 11 18 11.4477 18 12C17.9999 15.3137 15.3137 18 12 18C8.68631 18 6.00004 15.3137 6 12C6 8.68628 8.68629 5.99999 12 5.99999ZM17.6562 2.10057C18.0468 1.71005 18.6807 1.71005 19.0713 2.10057C19.4614 2.49105 19.4615 3.12419 19.0713 3.51463L18.3633 4.22069L18.3642 4.22167C17.9737 4.61219 17.9737 5.2452 18.3642 5.63573C18.7548 6.02612 19.3878 6.02621 19.7783 5.63573L20.4853 4.9287C20.8759 4.53839 21.5089 4.53826 21.8994 4.9287C22.2899 5.31915 22.2897 5.95222 21.8994 6.34276L19.7783 8.46483C19.5909 8.65223 19.3363 8.75671 19.0713 8.75682H16.6572L12.707 12.707C12.3165 13.0974 11.6834 13.0974 11.293 12.707C10.9025 12.3165 10.9026 11.6835 11.293 11.293L15.2422 7.34374V4.9287C15.2422 4.66356 15.3477 4.40916 15.5351 4.22167L17.6562 2.10057Z"
        fill="#F78711"
      />
    </svg>
  );
}

function BellSmallIcon(): JSX.Element {
  return (
    <svg aria-hidden="true" width="18" height="18" viewBox="0 0 40 40" fill="none">
      <path
        d="M30 28H10V26H11V19.0314C11 14.0435 15.0294 10 20 10C24.9706 10 29 14.0435 29 19.0314V26H30V28ZM13 26H27V19.0314C27 15.1481 23.866 12 20 12C16.134 12 13 15.1481 13 19.0314V26ZM17.5 29H22.5C22.5 30.3807 21.3807 31.5 20 31.5C18.6193 31.5 17.5 30.3807 17.5 29Z"
        fill="#2563EB"
      />
    </svg>
  );
}

function GlobeIcon(): JSX.Element {
  return (
    <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none">
      <path
        d="M12.5065 3.62326L11.4835 5.39501C8.57378 4.51629 5.96968 4.94531 5.07207 6.50001C3.89477 8.53915 5.86239 12.1524 9.75027 14.3971C13.6382 16.6418 17.7512 16.5392 18.9285 14.5C19.8261 12.9453 18.8956 10.4756 16.6797 8.39501L17.7026 6.62326C20.7847 9.33196 22.1654 12.8934 20.6605 15.5C18.8003 18.7221 13.4717 18.8551 8.75027 16.1292C4.0289 13.4033 1.47976 8.72208 3.34002 5.50001C4.84492 2.89344 8.61964 2.30849 12.5065 3.62326ZM15.8842 1.77277L17.6163 2.77277L12.6163 11.433L10.8842 10.433L15.8842 1.77277ZM6.73233 20H17.0003V22H5.01761C4.94008 22.0014 4.86194 21.9938 4.78481 21.9768C4.77025 21.9735 4.7558 21.97 4.74147 21.9662C4.6589 21.944 4.57784 21.9108 4.50028 21.866C4.47106 21.8492 4.44301 21.831 4.41616 21.8118C4.30161 21.7292 4.20524 21.623 4.1342 21.5003C4.06328 21.3772 4.01939 21.2404 4.00518 21.0997C4.00446 21.0924 4.00381 21.085 4.00325 21.0777C3.98786 20.883 4.02924 20.6819 4.13425 20.5L6.38425 16.6029L8.1163 17.6029L6.73233 20Z"
        fill="#1DAF61"
      />
    </svg>
  );
}

function DownloadIcon(): JSX.Element {
  return (
    <svg
      aria-hidden="true"
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  );
}

interface MetricCardProps {
  icon: React.ReactNode;
  iconBg: string;
  iconColor: string;
  label: string;
  value: number;
  loading: boolean;
}

function MetricCard({
  icon,
  iconBg,
  iconColor,
  label,
  value,
  loading,
}: MetricCardProps): JSX.Element {
  return (
    <div
      style={{
        flex: 1,
        background: '#fff',
        border: '1px solid #E2E8F0',
        borderRadius: 12,
        padding: '14px 16px',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        boxSizing: 'border-box',
        minWidth: 0,
      }}
    >
      {loading ? (
        <>
          <div
            className="skeleton-box"
            style={{ width: 36, height: 36, borderRadius: 10, flexShrink: 0 }}
          />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="skeleton-box" style={{ width: 80, height: 11, marginBottom: 8 }} />
            <div className="skeleton-box" style={{ width: 40, height: 22 }} />
          </div>
        </>
      ) : (
        <>
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: 10,
              background: iconBg,
              color: iconColor,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            {icon}
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 12, color: '#64748B' }}>{label}</div>
            <div style={{ fontSize: 24, fontWeight: 800, color: '#0F172A' }}>{value}</div>
          </div>
        </>
      )}
    </div>
  );
}

function downloadNexusCsv(states: StateNexusData[]): void {
  const header = [
    'State Code',
    'State Name',
    'Nexus Triggered',
    'Revenue',
    'Revenue Threshold',
    'Threshold %',
    'Registration Status',
    'Filing Status',
  ];
  const rows = states.map((s) => [
    s.stateCode,
    s.stateName,
    s.nexusTriggered ? 'Yes' : 'No',
    String(s.revenue),
    String(s.revenueThreshold),
    s.revenueThresholdPct.toFixed(1),
    s.registrationStatus,
    s.filingStatus,
  ]);
  const csv = [header, ...rows]
    .map((r) => r.map((v) => `"${v.replace(/"/g, '""')}"`).join(','))
    .join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'nexus-status.csv';
  a.click();
  URL.revokeObjectURL(url);
}

export default function NexusStatus(): JSX.Element {
  const { organizationId } = useAuth();
  const w = useWindowWidth();
  const mobile = w < 768;
  const tableRef = useRef<HTMLDivElement>(null);
  const [states, setStates] = useState<StateNexusData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [errorStatus, setErrorStatus] = useState<number | null>(null);
  const [selectedCode, setSelectedCode] = useState<string | null>(null);
  const [alertCount, setAlertCount] = useState(0);
  const [dismissedStateCodes, setDismissedStateCodes] = useState<Set<string>>(new Set());

  const load = useCallback((): void => {
    setLoading(true);
    setError(null);
    setErrorStatus(null);
    Promise.allSettled([
      nexusStatusApi.getStatus(organizationId),
      stacCountsApi.nexusAlertsCount(organizationId),
    ])
      .then(([statusRes, alertsCountRes]) => {
        if (statusRes.status === 'fulfilled') {
          const data: StateNexusData[] = Array.isArray(statusRes.value) ? statusRes.value : [];
          setStates(data);
          setSelectedCode((prev) => {
            if (prev) return prev;
            const triggered = data.find(
              (s) => s.nexusTriggered && s.registrationStatus !== 'registered',
            );
            const watching = data.find((s) => s.revenueThresholdPct >= 80);
            return triggered?.stateCode ?? watching?.stateCode ?? null;
          });
        } else {
          const reason = statusRes.reason;
          setError(reason instanceof Error ? reason.message : 'Failed to load');
          setErrorStatus(reason instanceof ApiError ? reason.status : null);
        }
        if (alertsCountRes.status === 'fulfilled') {
          setAlertCount(alertsCountRes.value.count ?? 0);
        }
      })
      .finally(() => setLoading(false));
  }, [organizationId]);

  useEffect(load, [load]);

  function handleDismissState(stateCode: string): void {
    setDismissedStateCodes((prev) => new Set([...prev, stateCode]));
  }

  const selectedState = states.find((s) => s.stateCode === selectedCode) ?? null;

  const counts = states.reduce<Counts>(
    (acc, s) => {
      if (s.nexusTriggered) acc.triggered++;
      else if (s.revenueThresholdPct >= 80) acc.watching++;
      return acc;
    },
    { triggered: 0, watching: 0 },
  );

  const metricCards: MetricCardProps[] = [
    {
      icon: <WarningTriangleIcon />,
      iconBg: '#FFEBEC',
      iconColor: '#E93544',
      label: 'Triggered States',
      value: counts.triggered,
      loading,
    },
    {
      icon: <TargetIcon />,
      iconBg: '#FFF1EB',
      iconColor: '#F78711',
      label: 'Approaching',
      value: counts.watching,
      loading,
    },
    {
      icon: <BellSmallIcon />,
      iconBg: '#EFF6FF',
      iconColor: '#2563EB',
      label: 'Active Alerts',
      value: alertCount,
      loading,
    },
    {
      icon: <GlobeIcon />,
      iconBg: '#E0FAEC',
      iconColor: '#1DAF61',
      label: 'States Tracked',
      value: states.length,
      loading,
    },
  ];

  return (
    <div
      style={{
        padding: '18px 22px',
        display: 'flex',
        flexDirection: 'column',
        gap: 0,
        height: '100%',
        boxSizing: 'border-box',
        overflow: 'auto',
        background: '#F8FAFC',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          marginBottom: 16,
          gap: 8,
          flexShrink: 0,
          flexWrap: mobile ? 'wrap' : 'nowrap',
        }}
      >
        <div>
          <div style={{ fontSize: 20, fontWeight: 800, color: '#0F172A' }}>Nexus Status</div>
          <div style={{ fontSize: 13, color: '#64748B', marginTop: 2 }}>
            Monitor nexus thresholds and required actions.
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
          <button
            type="button"
            onClick={load}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 5,
              padding: '6px 13px',
              background: '#fff',
              border: '1px solid #E2E8F0',
              borderRadius: 8,
              fontSize: 12,
              fontWeight: 600,
              color: '#475569',
              cursor: 'pointer',
            }}
          >
            <svg
              aria-hidden="true"
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="23 4 23 10 17 10" />
              <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
            </svg>
            Refresh
          </button>
          <button
            type="button"
            onClick={() => downloadNexusCsv(states)}
            disabled={states.length === 0}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              padding: '7px 16px',
              background: states.length === 0 ? '#94A3B8' : '#2563EB',
              border: 'none',
              borderRadius: 8,
              fontSize: 12,
              fontWeight: 700,
              color: '#fff',
              cursor: states.length === 0 ? 'default' : 'pointer',
            }}
          >
            <DownloadIcon />
            Download
          </button>
        </div>
      </div>

      {!loading && !error && states.length === 0 && (
        <div
          style={{
            background: '#FFFBEB',
            border: '1px solid #FDE68A',
            borderRadius: 8,
            padding: '10px 16px',
            marginBottom: 14,
            fontSize: 12,
            color: '#92400E',
          }}
        >
          No nexus data yet — connect a sales channel to start tracking exposure.
        </div>
      )}

      <div
        style={{
          display: 'flex',
          flexWrap: mobile ? 'wrap' : 'nowrap',
          gap: 14,
          marginBottom: 14,
          flexShrink: 0,
        }}
      >
        {metricCards.map((c) => (
          <div key={c.label} style={{ flex: mobile ? '0 0 calc(50% - 7px)' : 1, minWidth: 0 }}>
            <MetricCard {...c} />
          </div>
        ))}
      </div>

      <div
        style={{
          display: 'flex',
          flexDirection: mobile ? 'column' : 'row',
          gap: 14,
          flexShrink: 0,
          alignItems: 'flex-start',
          marginBottom: 14,
        }}
      >
        <div
          style={{
            flex: '0 0 auto',
            background: '#fff',
            border: '1px solid #E2E8F0',
            borderRadius: 12,
            padding: '16px 14px 10px',
            width: mobile ? '100%' : undefined,
            boxSizing: 'border-box',
          }}
        >
          <USNexusMap
            states={states}
            loading={loading}
            error={error}
            errorStatus={errorStatus}
            onRetry={load}
            selectedCode={selectedCode}
            onStateClick={(s) => setSelectedCode(s.stateCode)}
          />
        </div>
        <div
          style={{
            flex: 1,
            minWidth: 0,
            width: mobile ? '100%' : undefined,
            background: '#fff',
            border: '1px solid #E2E8F0',
            borderRadius: 12,
            overflow: 'hidden',
          }}
        >
          <StateDetailPanel stateData={selectedState} />
        </div>
      </div>

      <div ref={tableRef} style={{ flexShrink: 0 }}>
        <NexusAlertsTable
          states={states}
          dismissedCodes={dismissedStateCodes}
          loading={loading}
          error={error}
          errorStatus={errorStatus}
          onRefresh={load}
          onDismiss={handleDismissState}
        />
      </div>
    </div>
  );
}
